import { useCallback, useEffect, useRef } from 'react';

/*
    Pointer-events based so it works with a mouse, a trackpad and a pen without
    three code paths. Capture keeps the drag alive when the pointer outruns the
    handle, which is what makes fast drags feel solid instead of sticky.
*/

export function useDrag(onMove: (dx: number, dy: number) => void) {
    const origin = useRef<{ x: number; y: number } | null>(null);
    const moveRef = useRef(onMove);
    moveRef.current = onMove;

    const onPointerDown = useCallback((e: React.PointerEvent) => {
        e.preventDefault();
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        origin.current = { x: e.clientX, y: e.clientY };
    }, []);

    const onPointerMove = useCallback((e: React.PointerEvent) => {
        if (!origin.current) return;
        const dx = e.clientX - origin.current.x;
        const dy = e.clientY - origin.current.y;
        origin.current = { x: e.clientX, y: e.clientY };
        moveRef.current(dx, dy);
    }, []);

    const onPointerUp = useCallback((e: React.PointerEvent) => {
        origin.current = null;
        (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    }, []);

    return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp };
}

/** Divider between two panes. `axis` is the direction it moves in. */
export default function Resizer({
    axis,
    onResize,
}: {
    axis: 'x' | 'y';
    onResize: (delta: number) => void;
}) {
    const handlers = useDrag((dx, dy) => onResize(axis === 'x' ? dx : dy));

    // While dragging, stop the whole document selecting text.
    useEffect(() => {
        const stop = (e: Event) => e.preventDefault();
        document.addEventListener('selectstart', stop);
        return () => document.removeEventListener('selectstart', stop);
    }, []);

    return (
        <div
            {...handlers}
            role="separator"
            aria-orientation={axis === 'x' ? 'vertical' : 'horizontal'}
            style={{
                flex: '0 0 auto',
                position: 'relative',
                zIndex: 4,
                touchAction: 'none',
                cursor: axis === 'x' ? 'col-resize' : 'row-resize',
                ...(axis === 'x'
                    ? { width: 5, marginLeft: -2, marginRight: -2, alignSelf: 'stretch' }
                    : { height: 5, marginTop: -2, marginBottom: -2 }),
            }}
        >
            <span
                style={{
                    position: 'absolute',
                    inset: axis === 'x' ? '0 2px' : '2px 0',
                    background: 'transparent',
                    transition: 'background 120ms',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--accent)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                }}
            />
        </div>
    );
}
